import React from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { RangeSelectorControlProps, rangeControlChanged } from "@hooks";
import {
  BookingTypeSetting,
  MobileListHeader,
  MobileListSettingsButton,
  MonthGroupedList,
  PeriodSetting,
  SettingsFieldGroup,
  SettingsSections,
  SortSelect,
  useDateAmountSortOptions,
  type BookingTypeFilter,
  type DateAmountSortField,
} from "@components/MobileList";
import { TableBookingData } from "../BookingsSingleInvestmentTable";
import { InvestmentBookingMobileRow } from "./InvestmentBookingMobileRow";

const bookingDate = (booking: TableBookingData) => booking.valueDate;
const bookingId = (booking: TableBookingData) => booking.id;
const renderBooking = (booking: TableBookingData) => (
  <InvestmentBookingMobileRow booking={booking} />
);

interface Props {
  bookings: TableBookingData[];
  totalCount?: number;
  rangeProps: RangeSelectorControlProps;
  types: BookingTypeFilter[];
  onTypesChange: (types: BookingTypeFilter[]) => void;
  sortField: DateAmountSortField;
  sortDirection: TableSortDirection;
  onSort: (field: DateAmountSortField, direction: TableSortDirection) => void;
  isRefreshing: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  showEndOfList: boolean;
}

export const InvestmentProtocolMobileList = ({
  bookings,
  totalCount,
  rangeProps,
  types,
  onTypesChange,
  sortField,
  sortDirection,
  onSort,
  isRefreshing,
  hasMore,
  isLoadingMore,
  onLoadMore,
  showEndOfList,
}: Props) => {
  const { t } = useTranslation();
  const sortOptions = useDateAmountSortOptions();
  const hasChanges =
    rangeControlChanged(rangeProps) ||
    types.length > 0 ||
    sortField !== "valueDate" ||
    sortDirection !== "DESC";

  return (
    <Box
      data-testid="investment-protocol-mobile-list"
      aria-busy={isRefreshing}
      sx={{ opacity: isRefreshing ? 0.6 : 1 }}
    >
      <MobileListHeader
        count={
          totalCount !== undefined &&
          t("components.depotsStatistic.bookingsTable.mobile.count", {
            count: totalCount,
          })
        }
      >
        <MobileListSettingsButton
          hasChanges={hasChanges}
          canReset={hasChanges}
          onReset={() => {
            rangeProps.selectInterval(rangeProps.defaultIntervalType);
            onTypesChange([]);
            onSort("valueDate", "DESC");
          }}
        >
          <SettingsSections testId="investment-protocol-settings">
            <SettingsFieldGroup>
              <PeriodSetting rangeProps={rangeProps} />
              <BookingTypeSetting selected={types} onChange={onTypesChange} />
              <SortSelect
                label={t("domains.investments.investmentTable.mobile.sortBy")}
                options={sortOptions}
                field={sortField}
                direction={sortDirection}
                onChange={onSort}
              />
            </SettingsFieldGroup>
          </SettingsSections>
        </MobileListSettingsButton>
      </MobileListHeader>

      <MonthGroupedList
        items={bookings}
        getDate={bookingDate}
        getId={bookingId}
        renderItem={renderBooking}
        groupByMonth={sortField === "valueDate"}
        emptyText={t("investment.protocolTable.noBookings")}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
        loadingMoreText={t(
          "components.depotsStatistic.bookingsTable.mobile.loadingMore"
        )}
        endText={
          showEndOfList &&
          t("components.depotsStatistic.bookingsTable.mobile.allLoaded")
        }
        withLeading={false}
      />
    </Box>
  );
};
